import ColumnSelectorButton from "@via/components/column-selector-button/column-selector-button";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import {
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  type SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { EMPTY_TABLE_MESSAGE } from "@via/utils/constants.tsx";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import OverflowTooltipContent from "@via/components/overflow-tooltip-content";
import React, {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Tooltip } from "react-tooltip";
import { SkeletonLoader } from "@via/components/loading/loading";

export enum TableSize {
  Small = "small",
  Big = "big",
}

export enum DrawerSize {
  Small = "small",
  Medium = "medium",
  Big = "big",
}

interface TableProps<D extends object> {
  isLoading?: boolean;
  loaderCount?: number;
  data?: D[];
  columns: Array<ColumnDef<D>>;
  enableSorting?: boolean;
  sort?: SortingState;
  setSort?: (sort: SortingState) => void;
  page?: number;
  setPage?: (page: number) => void;
  pageSize?: number;
  setPageSize?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  total?: number;
  emptyMessage?: string | ReactNode;
  showPagination?: boolean;
  size?: TableSize;
  onRowClick?: (row: D) => void;
  onRowHover?: (row: D | null) => void;
  onRowHoverStyling?: string;
  rowTooltipContent?: string | null;
  applyColumnSize?: boolean;
  enableColumnResizing?: boolean;
  enableGlobalFilter?: boolean;
  globalFilterPlaceholder?: string;
  additionalFilters?: ReactNode;
  globalFilter?: string;
  setGlobalFilter?: (filter: string) => void;
  onFocusGlobalFilter?: () => void;
  customTableClassName?: string;
  displayCustomColumnVisibility?: boolean;
  initialVisibleColumns?: string[];
  initialSort?: SortingState;
  resetPageIndexOnDataChange?: boolean;
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (newVisibility) => void;
  getRowId?: (row: D) => string;
  /** The row to highlight, compared with the id getRowId (or row.id) gives. Row class only. */
  highlightRowId?: string | null;
  expandedRows?: Set<string>;
  onExpandedRowsChange?: (expanded: Set<string>) => void;
  renderExpandedContent?: (row: D) => ReactNode;
}

const Table: FC<TableProps<any>> = ({
  isLoading = false,
  loaderCount = 12,
  data = [],
  columns,
  enableSorting = true,
  initialSort,
  sort,
  setSort,
  page,
  setPage,
  pageSize,
  setPageSize,
  pageSizeOptions = [5, 10, 20],
  total,
  emptyMessage = EMPTY_TABLE_MESSAGE.DEFAULT,
  showPagination = true,
  size = TableSize.Big,
  onRowClick,
  onRowHover,
  onRowHoverStyling,
  rowTooltipContent,
  applyColumnSize,
  enableColumnResizing = false,
  enableGlobalFilter = false,
  globalFilterPlaceholder = "Search",
  additionalFilters,
  globalFilter,
  setGlobalFilter,
  onFocusGlobalFilter,
  customTableClassName = "",
  displayCustomColumnVisibility = false,
  resetPageIndexOnDataChange = false,
  columnVisibility,
  onColumnVisibilityChange,
  getRowId,
  highlightRowId,
  expandedRows,
  onExpandedRowsChange,
  renderExpandedContent,
}) => {
  const [uncontrolledPage, setUncontrolledPage] = useState(0);
  const [uncontrolledPageSize, setUncontrolledPageSize] = useState(10);

  const [uncontrolledSort, setUncontrolledSort] = useState<SortingState>(
    initialSort ?? []
  );
  const [unControlledGlobalFilter, setUncontrolledGlobalFilter] =
    useState<string>("");
  // Text shown in the box, updated on every keystroke so typing stays snappy.
  // The value actually handed to TanStack (which re-filters every row) is
  // debounced, so a 15k-row report table is filtered once per pause, not once
  // per character.
  const [filterInputValue, setFilterInputValue] = useState<string>("");
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  // Reserved for future uncontrolled mode support (when expansion is enabled but not controlled)
  const [uncontrolledExpandedRows, _setUncontrolledExpandedRows] = useState<
    Set<string>
  >(new Set());

  const effectiveColumnVisibility = columnVisibility;

  // Determine if expansion is enabled (only if renderExpandedContent is provided)
  const isExpansionEnabled = !!renderExpandedContent;

  // Determine if expansion is controlled externally
  const isExpansionControlled =
    expandedRows !== undefined && onExpandedRowsChange !== undefined;

  // Get effective expanded rows state
  const effectiveExpandedRows = isExpansionControlled
    ? expandedRows
    : uncontrolledExpandedRows;

  // Get row ID function (defaults to row.id or index)
  const getEffectiveRowId = useCallback(
    (row: any, index: number): string => {
      if (getRowId) {
        return getRowId(row);
      }
      // Try to get id from row object
      if (row && typeof row === "object" && "id" in row) {
        return String(row.id);
      }
      // Fallback to index
      return String(index);
    },
    [getRowId]
  );

  const handleColumnVisibilityChange: OnChangeFn<
    VisibilityState
  > = newVisibility => {
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange(newVisibility);
    }
  };

  //Determine if global filtering is controlled externally
  const isGlobalFilterControlled =
    globalFilter !== undefined && setGlobalFilter !== undefined;
  const filtering = isGlobalFilterControlled
    ? globalFilter
    : unControlledGlobalFilter;
  const setFiltering = isGlobalFilterControlled
    ? setGlobalFilter
    : setUncontrolledGlobalFilter;

  // Tracks whether a debounce timer is currently armed for a user-originated
  // edit (i.e. `filterInputValue` has diverged from `filtering` and hasn't
  // been committed yet). Chosen race semantics: while an edit is in flight,
  // the user's typed text always wins over an external filter change that
  // lands mid-debounce - it is never silently discarded. Once the debounce
  // commits (or is superseded by further typing that later settles), this
  // clears and external changes are free to sync into the box again. See
  // the "Finding 1" test in table-global-filter.test.tsx for the exact race
  // this defends against.
  const pendingEditRef = useRef(false);

  useEffect(() => {
    // Skip the sync while the user has an unflushed edit pending (see
    // pendingEditRef above) - otherwise an external filter change arriving
    // mid-keystroke would snap the box back and discard what was typed.
    if (pendingEditRef.current) {
      return;
    }
    setFilterInputValue(filtering ?? "");
  }, [filtering]);

  // This effect intentionally depends only on `filterInputValue`, even
  // though its body also reads `filtering` and `setFiltering`. That is not
  // an oversight: it must fire exactly once per local edit (arm a timer,
  // then either fire it or have it superseded by the next keystroke), and it
  // must NOT re-run just because `filtering` changed for some other reason
  // (e.g. an external reset) - re-running here would re-evaluate against a
  // now-stale `filterInputValue` closure or re-arm a timer for a change the
  // user didn't make. Keeping `filtering` in sync for other reasons is the
  // job of the effect above, which is declared first and therefore always
  // runs before this one within the same commit/render pass - the ordering
  // is load-bearing. If these two effects were ever reordered, or if
  // `filtering`/`setFiltering` were added to this dependency array, the
  // mount-time and post-commit ping-pong analysis in the Task 4 report would
  // need to be redone, since this effect could then fire before the sync
  // effect has reconciled `filterInputValue`.
  useEffect(() => {
    if ((filtering ?? "") === filterInputValue) {
      pendingEditRef.current = false;
      return;
    }
    pendingEditRef.current = true;
    const timeout = setTimeout(() => {
      pendingEditRef.current = false;
      setFiltering(filterInputValue);
    }, 250);
    return () => clearTimeout(timeout);
  }, [filterInputValue]);

  // Determine if sorting is controlled externally
  const isSortingControlled = sort !== undefined && setSort !== undefined;
  const sorting = isSortingControlled ? sort : uncontrolledSort;
  const setSorting = isSortingControlled ? setSort : setUncontrolledSort;

  // Determine if pagination is controlled externally
  const isPaginationControlled = page !== undefined && setPage !== undefined;
  // Use external or internal pagination based on control mode
  const currentPage = isPaginationControlled ? page : uncontrolledPage;
  const setCurrentPage = isPaginationControlled ? setPage : setUncontrolledPage;
  const currentPageSize =
    pageSize !== undefined ? pageSize : uncontrolledPageSize;
  const setCurrentPageSize =
    setPageSize !== undefined ? setPageSize : setUncontrolledPageSize;

  const pageCount = Math.ceil((total ?? data.length) / currentPageSize);

  useEffect(() => {
    if (isPaginationControlled && total == null) return;
    const maxPage = Math.max(0, pageCount - 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [pageCount, currentPage, setCurrentPage, isPaginationControlled, total]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setCurrentPage(0);
      setCurrentPageSize(newPageSize);
    },
    [setCurrentPageSize]
  );

  const handleSortChange = useCallback(
    newSort => {
      if (typeof newSort === "function") {
        setSorting(newSort);
      }
    },
    [setSorting]
  );

  const table = useReactTable({
    data,
    columns,
    manualPagination: isPaginationControlled,
    pageCount, // Total pages
    enableSorting: enableSorting,
    enableColumnResizing,
    columnResizeMode: "onChange",
    enableRowSelection: true,
    state: {
      sorting,
      pagination: { pageIndex: currentPage, pageSize: currentPageSize },
      globalFilter: filtering,
      columnVisibility: effectiveColumnVisibility,
      columnSizing,
    },
    onPaginationChange: updater => {
      // INFO: Parameter type is Updater<PaginationState> and Updater can be either a value or a function
      // @tanstack/table-core/src/types.ts: export type Updater<T> = T | ((old: T) => T)
      // So, to avoid TS error, we need to check if updater is a function or not before updating the state
      if (typeof updater !== "function") {
        setCurrentPage(updater.pageIndex);
        setCurrentPageSize(updater.pageSize);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableGlobalFilter,
    manualSorting: isSortingControlled,
    manualFiltering: isGlobalFilterControlled,
    enableSortingRemoval: true,
    onSortingChange: handleSortChange,
    onGlobalFilterChange: setFiltering,
    onColumnSizingChange: setColumnSizing,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
  });

  useEffect(() => {
    if (resetPageIndexOnDataChange) {
      handlePageChange(0);
    }
  }, [data, resetPageIndexOnDataChange]);

  return (
    <div className="table-root">
      <div className="flex w-full flex-wrap items-center gap-3">
        {enableGlobalFilter && (
          <div className="relative mt-2 flex items-center rounded-md bg-white shadow-sm ring-1 ring-inset ring-gray-300">
            <input
              type="text"
              className="rounded-md border-0 py-1.5 pl-9 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-200 ease-in-out placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder={globalFilterPlaceholder}
              value={filterInputValue}
              onFocus={onFocusGlobalFilter}
              onChange={e => {
                setFilterInputValue(e.target.value);
              }}
            />
            <MagnifyingGlassIcon
              className="absolute left-2 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        )}
        {additionalFilters && additionalFilters}
        {displayCustomColumnVisibility && (
          <div className="ml-auto mt-2">
            <ColumnSelectorButton
              columns={table
                .getAllColumns()
                .filter(column => column?.id !== "checkboxColumn")
                .map(column => ({
                  id: column.id,
                  // @ts-expect-error: column type seems any, we'll fix it later
                  label: column?.columnDef?.header() || column?.id,
                }))}
              columnVisibility={table.getAllColumns().reduce(
                (acc, column) => {
                  acc[column.id] = column.getIsVisible();
                  return acc;
                },
                {} as Record<string, boolean>
              )}
              onToggle={(columnId, isVisible) => {
                const column = table
                  .getAllColumns()
                  .find(col => col.id === columnId);
                if (column) {
                  column.toggleVisibility(isVisible);
                }
              }}
              excludeColumnIds={["checkboxColumn"]}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={loaderCount} />
        </div>
      ) : table.getRowModel().rows.length === 0 && size === TableSize.Big ? (
        <div className="via-generic-error">
          <EmptyDataIcon />
          {emptyMessage}
        </div>
      ) : table.getRowModel().rows.length === 0 && size === TableSize.Small ? (
        <div className="flex flex-col py-2 text-sm font-normal text-gray-500">
          {emptyMessage}
        </div>
      ) : (
        <>
          <div className="inline-block min-w-full py-2 align-middle">
            <table
              className={classNames(
                "min-w-full border-separate border-spacing-0",
                customTableClassName
              )}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={classNames(
                          header.column.getIsSorted()
                            ? "text-indigo-600 hover:text-indigo-600"
                            : "",
                          header.column.id === "stickyColumn"
                            ? "sticky right-0 z-10 bg-white"
                            : "",
                          enableColumnResizing &&
                            header.colSpan === 1 &&
                            header.column.getCanResize()
                            ? "relative"
                            : "",
                          header.isPlaceholder ? "py-0" : "py-3.5",
                          "sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 pr-3 text-left text-sm font-semibold text-gray-500 backdrop-blur backdrop-filter duration-150 ease-in-out hover:text-gray-700"
                        )}
                        style={
                          applyColumnSize && header.colSpan === 1
                            ? {
                                width: header.getSize(),
                              }
                            : undefined
                        }>
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none flex min-w-[36px]"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <span className="pl-2">↑</span>,
                              desc: <span className="pl-2">↓</span>,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                        {enableColumnResizing &&
                          header.colSpan === 1 &&
                          header.column.getCanResize() &&
                          !header.isPlaceholder && (
                            <div
                              onMouseDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onDoubleClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                // "Auto resize" = reset to default column size.
                                header.column.resetSize();
                              }}
                              className={classNames(
                                "absolute right-0 top-0 h-full w-3 cursor-col-resize select-none touch-none flex items-center justify-center",
                                header.column.getIsResizing()
                                  ? "bg-indigo-50"
                                  : "bg-white/40 hover:bg-gray-100"
                              )}>
                              <div
                                className={classNames(
                                  "h-6 border-l border-dotted pointer-events-none",
                                  header.column.getIsResizing()
                                    ? "border-indigo-500"
                                    : "border-gray-300"
                                )}
                              />
                            </div>
                          )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => {
                  const rowId = getEffectiveRowId(row.original, rowIndex);
                  const isRowExpanded =
                    isExpansionEnabled && effectiveExpandedRows.has(rowId);
                  // Use visible leaf cell count so expanded content spans full width,
                  // even when the table uses grouped columns.
                  const columnCount = row.getVisibleCells().length;

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        id={`table-row-${rowIndex}`}
                        data-highlighted={
                          highlightRowId != null && rowId === highlightRowId
                            ? "true"
                            : undefined
                        }
                        className={classNames(
                          onRowHoverStyling || "",
                          "border-b hover:bg-gray-50",
                          highlightRowId != null && rowId === highlightRowId
                            ? "bg-amber-50 ring-2 ring-inset ring-amber-400"
                            : ""
                        )}
                        onMouseEnter={() => {
                          onRowHover && onRowHover(row.original);
                        }}
                        onMouseLeave={() => {
                          onRowHover && onRowHover(null);
                        }}>
                        {row.getVisibleCells().map((cell, index) => {
                          const meta = cell.column.columnDef.meta as
                            | { truncate?: boolean; tooltip?: boolean }
                            | undefined;
                          const shouldTruncate = Boolean(meta?.truncate);
                          const shouldTooltip = Boolean(meta?.tooltip);
                          const rawValue = cell.getValue();
                          const tooltipText =
                            rawValue === null || typeof rawValue === "undefined"
                              ? ""
                              : String(rawValue);
                          const tooltipId = `table-cell-tooltip-${row.id}-${cell.column.id}`;

                          return (
                            <td
                              key={`column_${index}`}
                              className={classNames(
                                size === TableSize.Big ? "py-4" : "py-2",
                                onRowClick ? "cursor-pointer" : "",
                                cell.column.id === "stickyColumn"
                                  ? "sticky right-0 z-10 bg-white"
                                  : "",
                                shouldTruncate
                                  ? "max-w-0 border-b border-gray-200 text-sm text-gray-500 sm:table-cell"
                                  : "whitespace-normal break-all border-b border-gray-200 text-sm text-gray-500 sm:table-cell"
                              )}
                              style={
                                applyColumnSize
                                  ? {
                                      width: cell.column.getSize(),
                                    }
                                  : undefined
                              }
                              onClick={() => {
                                onRowClick && onRowClick(row.original);
                              }}>
                              {shouldTruncate ? (
                                <OverflowTooltipContent
                                  tooltipId={tooltipId}
                                  tooltipText={tooltipText}
                                  enabled={shouldTooltip}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </OverflowTooltipContent>
                              ) : (
                                flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {/* Expanded content row */}
                      {isExpansionEnabled &&
                        isRowExpanded &&
                        renderExpandedContent && (
                          <tr key={`${row.id}-expanded`} className="border-b">
                            <td
                              colSpan={columnCount}
                              className="p-0 border-b border-gray-200">
                              {renderExpandedContent(row.original)}
                            </td>
                          </tr>
                        )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {/* Render Tooltip */}
            {table
              .getRowModel()
              .rows.map(
                row =>
                  rowTooltipContent && (
                    <Tooltip
                      key={row.id}
                      anchorSelect={`#table-row-${row.original.id}`}
                      content={rowTooltipContent}
                    />
                  )
              )}
          </div>

          {showPagination && total != undefined && total > 5 && (
            <div className="mb-8 mt-8 flex w-full flex-col items-center gap-2 text-xs sm:flex-row">
              <div className="mb-2 sm:mb-0 sm:mr-auto">
                <span className="mr-2">Items per page</span>
                <select
                  id="pagination"
                  className="w-12 rounded border border-gray-200 p-1 text-xs focus:ring-1 focus:ring-inset focus:ring-indigo-600"
                  value={table.getState().pagination.pageSize}
                  onChange={e => {
                    handlePageSizeChange(Number(e.target.value));
                  }}>
                  {pageSizeOptions.map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
                {
                  <span className="ml-2">
                    {currentPage * currentPageSize + 1} -{" "}
                    {Math.min((currentPage + 1) * currentPageSize, total)} of{" "}
                    {total}
                  </span>
                }
              </div>
              <div className="flex gap-2">
                {/* First Page Button */}
                <button
                  className={classNames(
                    !table.getCanPreviousPage()
                      ? "bg-gray-100"
                      : "hover:curstor-pointer bg-gray-100 hover:bg-gray-200 hover:text-indigo-600",
                    "rounded p-1 duration-300 ease-in-out"
                  )}
                  onClick={() => {
                    handlePageChange(0);
                  }}
                  disabled={!table.getCanPreviousPage()}>
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                </button>
                {/* Previous Page Button */}
                <button
                  className={classNames(
                    !table.getCanPreviousPage()
                      ? "bg-gray-100"
                      : "bg-gray-100 hover:cursor-pointer hover:bg-gray-200 hover:text-indigo-600",
                    "rounded p-1 duration-300 ease-in-out"
                  )}
                  onClick={() => {
                    handlePageChange(Math.max(currentPage - 1, 0));
                  }}
                  disabled={!table.getCanPreviousPage()}>
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                {/* Page Number Input */}
                <span className="flex items-center gap-1 text-xs">
                  <input
                    id="index"
                    min={1}
                    max={table.getPageCount()}
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={e => {
                      const pageNum = e.target.value
                        ? Number(e.target.value) - 1
                        : 0;
                      handlePageChange(pageNum);
                    }}
                    className="h-6 rounded border p-1 text-xs focus:ring-1 focus:ring-inset focus:ring-indigo-600"
                  />
                  of {table.getPageCount()}
                </span>
                {/* Next Page Button */}
                <button
                  className={classNames(
                    !table.getCanNextPage()
                      ? "bg-gray-100"
                      : "bg-gray-100 hover:cursor-pointer hover:bg-gray-200 hover:text-indigo-600",
                    "rounded p-1 duration-300 ease-in-out"
                  )}
                  onClick={() => {
                    handlePageChange(Math.min(currentPage + 1, pageCount - 1));
                  }}
                  disabled={!table.getCanNextPage()}>
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                {/* Last Page Button */}
                <button
                  className={classNames(
                    !table.getCanNextPage()
                      ? "bg-gray-100"
                      : "bg-gray-100 hover:cursor-pointer hover:bg-gray-200 hover:text-indigo-600",
                    "rounded p-1 duration-300 ease-in-out"
                  )}
                  onClick={() => {
                    handlePageChange(pageCount - 1);
                  }}
                  disabled={!table.getCanNextPage()}>
                  <ChevronDoubleRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Table;
